import {AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {FlatpickrOptions, Ng2FlatpickrComponent} from 'ng2-flatpickr';
import {Ukrainian} from 'flatpickr/dist/l10n/uk';
import {english} from 'flatpickr/dist/l10n/default';
import {checkDiffDates, periodRepetitionValidator} from '@shared/custom-validators';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup, ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {SchedulePlayingTypeEnum} from '@shared/enums/schedule-playing-type.enum';
import {ScheduleRepeatTypeEnum} from '@shared/enums/schedule-repeat-type.enum';
import * as moment from 'moment-timezone';
import {ScheduleService} from '@services/schedule/schedule.service';
import {debounceTime} from 'rxjs/operators';
import {ISchedule} from '@interfaces/schedule';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable} from 'rxjs';
import {Select} from '@ngxs/store';
import {UserSelectors} from '@store/user';
import {ITimezone} from '@interfaces/user';

@UntilDestroy()
@Component({
  selector: 'app-schedule-form',
  templateUrl: './schedule-form.component.html',
  styleUrls: ['./schedule-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScheduleFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ScheduleFormComponent),
      multi: true
    }
  ]
})
export class ScheduleFormComponent implements ControlValueAccessor, Validator, OnInit, AfterViewInit {
  @Select(UserSelectors.getTimezone) timezone$!: Observable<ITimezone>;

  @ViewChild('startPicker') pickerStart!: Ng2FlatpickrComponent;
  @ViewChild('endPicker') pickerEnd!: Ng2FlatpickrComponent;
  @ViewChild('allDayPicker') allPickerDay!: Ng2FlatpickrComponent;
  @ViewChild('endDateWeek') endDateWeek!: Ng2FlatpickrComponent;
  @ViewChild('endDateSpecific') endDateSpecific!: Ng2FlatpickrComponent;

  @Input() isHotPlaylist = false;

  public form!: UntypedFormGroup;
  public scheduleTypes = SchedulePlayingTypeEnum;
  public repetitionTypes = ScheduleRepeatTypeEnum;
  public dateTimeOptions: FlatpickrOptions = {
    enableTime: true,
    time_24hr: true,
    locale: this.setLanguage(),
    minuteIncrement: 1,
    minDate: new Date(),
    dateFormat: 'Y-m-d H:i',
  };
  public dateOptions: FlatpickrOptions = {
    minDate: new Date(),
    dateFormat: 'Y-m-d',
    locale: this.setLanguage(),
    time_24hr: true,
  };
  public weakDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  private timezone = '';
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
      private formBuilder: UntypedFormBuilder,
      private translate: TranslateService,
      private scheduleService: ScheduleService,
  ) {
  }
  ngOnInit(): void {
    this.form = this.formBuilder.group({
      scheduleMethod: this.initScheduleMethodFormGroup(),
      repetitionMethod: this.initRepetitionMethodFormGroup()
    }, { validators: [periodRepetitionValidator] });

    this.form.valueChanges.pipe(
        untilDestroyed(this)
    ).subscribe(value => {
      const payload = this.buildSchedulePayload();
      this.onChange(payload ?? null);
    });

    this.subscribeFormMethodsChanges();
    this.setScheduleType(this.isHotPlaylist ? this.scheduleTypes.period : this.scheduleTypes.constant);
    this.subscribeStartEndDateChanges();
    this.subscribeInputTotalChanges();
    this.getTimezone();
  }

  ngAfterViewInit(): void {
      const initialPayload = this.buildSchedulePayload();
      this.onChange(initialPayload ?? null);
  }

  writeValue(payload: any): void {
    if (!payload || !payload.scheduleType) return;

    setTimeout(() => {
      this.patchScheduleType(payload.scheduleType);

      switch (payload.scheduleType) {
        case SchedulePlayingTypeEnum.allday:
          this.setAllDay(payload);
          break;
        case (SchedulePlayingTypeEnum.period):
          if (payload.repetition) {
            this.setRepetitionDate(payload);
          } else {
            this.setPeriodDate(payload);
          }
          break;
        case SchedulePlayingTypeEnum.constant:
          break;
        case 'repetetive':
          this.setRepetitionDate(payload);
          break;
        case 'hot':
          if (payload.repetition) {
            this.setRepetitionDate(payload);
          } else {
            this.setPeriodDate(payload);
          }
          break;
      }
    }, 0);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { scheduleInvalid: true };
  }

  registerOnValidatorChange?(_: () => void): void {
    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.onChange(this.buildSchedulePayload());
    });
  }

  markAsTouched(): void {
    this.form.markAllAsTouched();
    this.onTouched();
  }

  private getTimezone(): void {
    this.timezone$.pipe(
        untilDestroyed(this),
    )
        .subscribe(timezone => {
          if (!timezone) return;
          this.timezone = timezone.name;
          // Set time for inputs date
          this.dateTimeOptions.minDate = moment().format('YYYY-MM-DD');
          this.dateOptions.minDate = moment().format('YYYY-MM-DD HH');
        });
  }

  private setScheduleType(type: SchedulePlayingTypeEnum): void {
    this.form.get('scheduleMethod.scheduleType')?.setValue(type);
  }
  private setLanguage(): object {
    const lang = this.translate.currentLang;
    const supportedLanguages = {
      ukr: Ukrainian,
      eng: english
    };

    return supportedLanguages[lang as keyof typeof supportedLanguages] || english;
  }

  private subscribeFormMethodsChanges(): void {
    // scheduleMethod controls
    const scheduleMethodCtrl = this.form.controls.scheduleMethod as any;
    const allDayCtrl = scheduleMethodCtrl.controls.allday;
    const startEndCtrl = scheduleMethodCtrl.controls.period;

    // repetitionMethod controls
    const repetitionMethodCtrl = this.form.controls.repetitionMethod as any;
    const weekDaysCtrl = repetitionMethodCtrl.controls.weekDays;
    const specificDaysCtrl = repetitionMethodCtrl.controls.specificDates;

    // initialize scheduleType changes stream
    const changesScheduleMethod$ = scheduleMethodCtrl.controls.scheduleType.valueChanges;
    const changesRepetitionMethod$ = repetitionMethodCtrl.controls.repetitionType.valueChanges;

    changesScheduleMethod$.subscribe((scheduleMethodType: string) => {
      if (scheduleMethodType === this.scheduleTypes.allday) {
        Object.keys(allDayCtrl.controls).forEach(key => {
          allDayCtrl.controls[key].setValidators(this.initAllDayModel()[key][1]);
          allDayCtrl.controls[key].updateValueAndValidity();
        });
        removeValidators(startEndCtrl);
        removeValidators(weekDaysCtrl);
        removeValidators(specificDaysCtrl);
      }

      if (scheduleMethodType === this.scheduleTypes.period || scheduleMethodType === 'hot') {
        Object.keys(startEndCtrl.controls).forEach(key => {
          startEndCtrl.controls[key].setValidators(this.initStartEndModel()[key][1]);
          startEndCtrl.controls[key].updateValueAndValidity();
        });
        removeValidators(allDayCtrl);
        removeValidators(weekDaysCtrl);
        removeValidators(specificDaysCtrl);
      }

      if (scheduleMethodType === this.scheduleTypes.constant) {
        removeValidators(allDayCtrl);
        removeValidators(startEndCtrl);
        removeValidators(weekDaysCtrl);
        removeValidators(specificDaysCtrl);
      }
    });

    changesRepetitionMethod$.subscribe((repetitionMethodType: string) => {
      if (repetitionMethodType === ScheduleRepeatTypeEnum.weekDays) {
        Object.keys(weekDaysCtrl.controls).forEach(key => {
          weekDaysCtrl.controls[key].setValidators(this.initWeekDaysModel()[key][1]);
          weekDaysCtrl.controls[key].updateValueAndValidity();
        });
        removeValidators(specificDaysCtrl);
        removeValidators(allDayCtrl);
      }

      if (repetitionMethodType === ScheduleRepeatTypeEnum.specificDates) {
        Object.keys(specificDaysCtrl.controls).forEach(key => {
          specificDaysCtrl.controls[key].setValidators(this.initSpecificDaysModal()[key][1]);
          specificDaysCtrl.controls[key].updateValueAndValidity();
        });
        removeValidators(weekDaysCtrl);
        removeValidators(allDayCtrl);
      }

      if (repetitionMethodType === ScheduleRepeatTypeEnum.noRepeat) {
        removeValidators(specificDaysCtrl);
        removeValidators(weekDaysCtrl);
        removeValidators(allDayCtrl);
      }
    });

    function removeValidators(controlsObj: { [key: string]: any }): void {
      Object.keys(controlsObj.controls).forEach(key => {
        controlsObj.controls[key].setValidators(null);
        controlsObj.controls[key].updateValueAndValidity();
      });
    }
  }

  private subscribeInputTotalChanges(): void {
    const changes$ = this.form.get('repetitionMethod.specificDates.totalDays')?.valueChanges;

    changes$?.pipe(debounceTime(100)).subscribe(days => {
      if (days > 365) this.form.get('repetitionMethod.specificDates.totalDays')?.patchValue('365', {emitEvent: false});
    });
  }

  // If period method active
  private subscribeStartEndDateChanges(): void {
    const startDateChanges$ = this.form.get('scheduleMethod.period.start')?.valueChanges;
    const endDateChanges$ = this.form.get('scheduleMethod.period.end')?.valueChanges;

    startDateChanges$?.subscribe(date => {
      if (!date) return;
      (this.pickerEnd.flatpickr as any).clear();
      const minDate = moment(new Date(date)).add(1, 'm').format('YYYY-MM-DD HH:mm');
      (this.pickerEnd.flatpickr as FlatpickrOptions).set({minDate});
    });

    endDateChanges$?.subscribe(date => {
      if (!date) return;
      const minDate = moment(new Date(date)).format('YYYY-MM-DD HH:mm');
      if (moment(minDate, 'YYYY-MM-DD HH:mm').isValid()) {
        (this.endDateWeek.flatpickr as FlatpickrOptions).set({minDate});
        (this.endDateSpecific.flatpickr as FlatpickrOptions).set({minDate});
      }
      (this.endDateSpecific.flatpickr as any).clear();
      (this.endDateWeek.flatpickr as any).clear();
    });
  }

  private initScheduleMethodFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      scheduleType: [''],
      period: this.formBuilder.group(this.initStartEndModel(), {validators: [checkDiffDates]}),
      allday: this.formBuilder.group(this.initAllDayModel()),
    });
  }

  private initRepetitionMethodFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      repetitionType: [this.repetitionTypes.noRepeat],
      weekDays: this.formBuilder.group(this.initWeekDaysModel()),
      specificDates: this.formBuilder.group(this.initSpecificDaysModal())
    });
  }

  private initAllDayModel(): { [key: string]: any } {
    const model = {
      date: ['', [Validators.required]]
    };
    return model;
  }

  private initStartEndModel(): { [key: string]: any } {
    const model = {
      start: ['', [Validators.required]],
      end: ['', [Validators.required]],
    };
    return model;
  }

  private initWeekDaysModel(): { [key: string]: any } {
    const model = {
      daysOfWeek: [['mon'], [Validators.required]],
      endDate: ['', [Validators.required]]
    };
    return model;
  }

  private initSpecificDaysModal(): { [key: string]: any } {
    const model = {
      totalDays: ['1', [Validators.required, Validators.min(1)]],
      endDate: ['', Validators.required]
    };
    return model;
  }

  onCheckBoxDayChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const days = this.form.get('repetitionMethod.weekDays.daysOfWeek')?.value;

    if (target.checked) {
      this.form.get('repetitionMethod.weekDays')?.patchValue({daysOfWeek: [target.value, ...days]});
    } else {
      this.form.get('repetitionMethod.weekDays')?.patchValue({daysOfWeek: days.filter((day: string) => day !== target.value)});
    }
  }

  private buildSchedulePayload(): {
    startLocal: string;
    endLocal: string;
    endLocalTimeStamp: string;
    scheduleType: SchedulePlayingTypeEnum | 'repetetive' | 'hot';
    repetition: string | null;
  } {
    const scheduleType = this.form.get('scheduleMethod.scheduleType')?.value;
    const repetitionType = this.form.get('repetitionMethod.repetitionType')?.value;

    if (scheduleType === this.scheduleTypes.allday) return this.processAllDay();
    else if (scheduleType === this.scheduleTypes.constant) return this.processConstant();
    else if (
        (scheduleType === this.scheduleTypes.period || scheduleType === 'hot')
        && repetitionType === this.repetitionTypes.noRepeat) return this.processPeriod();
    else if (
        (scheduleType === this.scheduleTypes.period || scheduleType === 'hot')
        && repetitionType === this.repetitionTypes.weekDays) return this.processWeekDays();
    else return this.processSpecificDates();
  }


  private processAllDay(): {
    startLocal: string;
    endLocal: string;
    endLocalTimeStamp: string;
    scheduleType: SchedulePlayingTypeEnum;
    repetition: null;
  } {
    const now = moment();
    const selectedDate = moment(this.form.get('scheduleMethod.allday.date')?.value[0]).format('YYYY-MM-DD');

    const startLocal = moment(selectedDate).set({
      hour: 0, minute: 0, second: 0, millisecond: 0
    }).format('YYYY-MM-DD HH:mm:ss');

    const endLocal = moment(selectedDate).set({
      hour: 23, minute: 59, second: 59, millisecond: 999
    }).format('YYYY-MM-DD HH:mm:ss');

    return {
      startLocal,
      endLocal,
      endLocalTimeStamp: endLocal,
      scheduleType: this.scheduleTypes.allday,
      repetition: null
    };
  }

  private processConstant(): {
  startLocal: string;
  endLocal: string;
  endLocalTimeStamp: string;
  scheduleType: SchedulePlayingTypeEnum;
  repetition: null;
} {
  const startLocal = moment().format('YYYY-MM-DD HH:mm:ss');
  const endLocal = moment(startLocal).add(100, 'y').format('YYYY-MM-DD HH:mm:ss');

  return {
    startLocal,
    endLocal,
    endLocalTimeStamp: endLocal,
    scheduleType: this.scheduleTypes.constant,
    repetition: null
  };
}

  private processPeriod(): {
    startLocal: string;
    endLocal: string;
    endLocalTimeStamp: string;
    scheduleType: SchedulePlayingTypeEnum | 'hot';
    repetition: null;
  } {
    const startLocal = moment(this.form.get('scheduleMethod.period.start')?.value[0]).format('YYYY-MM-DD HH:mm:ss');
    const endMoment = moment(this.form.get('scheduleMethod.period.end')?.value[0]);
    const endLocal = endMoment.seconds() === 0
        ? endMoment.clone().subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss')
        : endMoment.format('YYYY-MM-DD HH:mm:ss');

    return {
      startLocal,
      endLocal,
      endLocalTimeStamp: endLocal,
      scheduleType: this.isHotPlaylist ? 'hot' : this.scheduleTypes.period,
      repetition: null
    };
  }

  private processWeekDays(): {
    startLocal: string;
    endLocal: string;
    endLocalTimeStamp: string;
    scheduleType: 'repetetive' | 'hot';
    repetition: string;
  } {
    const startLocal = moment(this.form.get('scheduleMethod.period.start')?.value[0]).format('YYYY-MM-DD HH:mm:ss');
    const endMoment = moment(this.form.get('scheduleMethod.period.end')?.value[0]);
    const endLocal = endMoment.seconds() === 0
        ? endMoment.clone().subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss')
        : endMoment.format('YYYY-MM-DD HH:mm:ss');

    const weekDays = this.scheduleService.convertIntoUnixCronFormat(
        this.form.get('repetitionMethod.weekDays.daysOfWeek')?.value.join(','),
        'weekDays'
    );

    const endDate = moment(this.form.get('repetitionMethod.weekDays.endDate')?.value[0]).set({
      hour: moment(endLocal).hours(),
      minute: moment(endLocal).minutes(),
      second: moment(endLocal).seconds(),
      millisecond: moment(endLocal).milliseconds()
    }).format('YYYY-MM-DD HH:mm:ss');

    return {
      startLocal,
      endLocal: endDate,
      endLocalTimeStamp: endLocal,
      scheduleType: this.isHotPlaylist ? 'hot' : 'repetetive' as any,
      repetition: weekDays
    };
  }

  private processSpecificDates(): {
    startLocal: string;
    endLocal: string;
    endLocalTimeStamp: string;
    scheduleType: 'repetetive' | 'hot';
    repetition: string;
  } {
    const startLocal = moment(this.form.get('scheduleMethod.period.start')?.value[0]).format('YYYY-MM-DD HH:mm:ss');
    const endMoment = moment(this.form.get('scheduleMethod.period.end')?.value[0]);
    const endLocal = endMoment.seconds() === 0
        ? endMoment.clone().subtract(1, 'second').format('YYYY-MM-DD HH:mm:ss')
        : endMoment.format('YYYY-MM-DD HH:mm:ss');

    const daysCount = this.scheduleService.convertIntoUnixCronFormat(
        this.form.get('repetitionMethod.specificDates.totalDays')?.value,
        'daysCount'
    );

    const endDate = moment(this.form.get('repetitionMethod.specificDates.endDate')?.value[0]).set({
      hour: moment(endLocal).hours(),
      minute: moment(endLocal).minutes(),
      second: moment(endLocal).seconds(),
      millisecond: moment(endLocal).milliseconds()
    }).format('YYYY-MM-DD HH:mm:ss');

    return {
      startLocal,
      endLocal: endDate,
      endLocalTimeStamp: endLocal,
      scheduleType: this.isHotPlaylist ? 'hot' : 'repetetive' as any,
      repetition: daysCount
    };
  }

  private patchScheduleType(type: string): void {
    if (type === 'repetetive' || type === 'hot') {
      this.form.get('scheduleMethod.scheduleType')?.patchValue(SchedulePlayingTypeEnum.period);
    } else {
      this.form.get('scheduleMethod.scheduleType')?.patchValue(SchedulePlayingTypeEnum[type as SchedulePlayingTypeEnum]);
    }
  }

  private setRepetitionDate(initialData: Partial<ISchedule>): void {
    const repetition = initialData.repetition ? initialData.repetition : '';
    const data = this.scheduleService.convertFromUnixCronFormat(repetition);
    this.form.get('repetitionMethod.repetitionType')?.patchValue(data.type);

    setTimeout(() => {
      this.form.get('scheduleMethod.period.start')?.patchValue(
          [new Date(moment(initialData.startLocal).format('YYYY-MM-DD HH:mm:ss'))]);
      this.form.get('scheduleMethod.period.end')?.patchValue(
          [new Date(moment(initialData.endLocalTimestamp).format('YYYY-MM-DD HH:mm:ss'))]);

      (this.pickerStart.flatpickr as any).input.value = moment(initialData.startLocal).format('YYYY-MM-DD HH:mm:ss');
      (this.pickerEnd.flatpickr as any).input.value = moment(initialData.endLocalTimestamp).format('YYYY-MM-DD HH:mm:ss');

      if (data.type === 'weekDays') {
        this.form.get('repetitionMethod.weekDays')?.patchValue({daysOfWeek: data.data.toLowerCase().split(',')});
        this.form.get('repetitionMethod.weekDays.endDate')?.patchValue([new Date(moment(initialData.endLocal).format('YYYY-MM-DD HH:mm:ss'))]);
        (this.endDateWeek.flatpickr as any).input.value = moment(initialData.endLocal).format('YYYY-MM-DD');
      }

      if (data.type === 'specificDates') {
        this.form.get('repetitionMethod.specificDates.totalDays')?.patchValue(data.data);
        this.form.get('repetitionMethod.specificDates.endDate')?.patchValue([new Date(moment(initialData.endLocal).format('YYYY-MM-DD HH:mm:ss'))]);
        (this.endDateSpecific.flatpickr as any).input.value = moment(initialData.endLocal).format('YYYY-MM-DD');
      }
    }, 0);
  }

  private setPeriodDate(initialData: Partial<ISchedule>): void {
    setTimeout(() => {
      this.form.get('scheduleMethod.period.start')?.patchValue([new Date(moment(initialData.startLocal).format('YYYY-MM-DD HH:mm:ss'))]);
      this.form.get('scheduleMethod.period.end')?.patchValue([new Date(moment(initialData.endLocal).format('YYYY-MM-DD HH:mm:ss'))]);

      (this.pickerStart.flatpickr as any).input.value = moment(initialData.startLocal).format('YYYY-MM-DD HH:mm:ss');
      (this.pickerEnd.flatpickr as any).input.value = moment(initialData.endLocal).format('YYYY-MM-DD HH:mm:ss');
    }, 0);
  }

  private setAllDay(initialData: Partial<ISchedule>): void {
    setTimeout(() => {
      this.form.get('scheduleMethod.allday.date')?.patchValue([new Date(moment(initialData.startLocal).format('YYYY-MM-DD HH:mm:ss'))]);
      (this.allPickerDay.flatpickr as any).input.value = moment(initialData.startLocal).format('YYYY-MM-DD');
    }, 0);
  }
}
